/**
 * ErrorSuggestion - AI-powered error fix suggestions
 *
 * This component displays code execution errors with a "Fix with AI" button
 * that calls the chat API to get a suggested fix. Features:
 * - Clear error display with name and message
 * - Collapsible traceback for debugging
 * - "Fix with AI" button to get intelligent suggestions
 * - Streaming response from chat API
 * - "Apply Fix" button to insert suggested code into editor
 *
 * Design goal: Reduce frustration when errors occur by providing
 * helpful AI-powered suggestions that users can review and apply.
 */

import { useState, useCallback } from 'react';
import type { ExecutionError } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

interface ErrorSuggestionProps {
  /** The execution error to display */
  error: ExecutionError;

  /** The code that caused the error */
  code: string;

  /** Programming language for context in AI prompt */
  language: 'python' | 'javascript';

  /** Called when user clicks "Apply Fix" with the suggested code */
  onApplyFix?: (fixedCode: string) => void;

  /** Board ID for the chat API call */
  boardId: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ErrorSuggestion Component
 *
 * Displays an error with a button to request an AI fix suggestion.
 * The AI suggestion is fetched via the chat API and displayed inline.
 * Users can then apply the suggested fix to their code.
 */
export function ErrorSuggestion({
  error,
  code,
  language,
  onApplyFix,
  boardId,
}: ErrorSuggestionProps) {
  // Loading state while waiting for AI suggestion
  const [isLoading, setIsLoading] = useState(false);

  // The full AI response text
  const [suggestion, setSuggestion] = useState<string | null>(null);

  // The extracted code from the AI response
  const [suggestedCode, setSuggestedCode] = useState<string | null>(null);

  // ==========================================================================
  // REQUEST AI FIX
  // ==========================================================================

  /**
   * Calls the chat API to get an AI suggestion for fixing the error.
   * Uses SSE streaming to read the response progressively.
   */
  const requestFix = useCallback(async () => {
    // Set loading state and clear previous suggestion
    setIsLoading(true);
    setSuggestion(null);
    setSuggestedCode(null);

    try {
      // Call the chat API to get a fix suggestion
      // The prompt asks for a brief explanation and corrected code
      const response = await fetch(`/api/boards/${boardId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            {
              role: 'user',
              content: `Fix this ${language} code error.

Error: ${error.name}: ${error.value}
${error.traceback ? `\nTraceback:\n${error.traceback}` : ''}

Code:
\`\`\`${language}
${code}
\`\`\`

Respond with:
1. A brief explanation of what's wrong (1-2 sentences)
2. The corrected code in a code block

Keep your response concise.`,
            },
          ],
          modelSettings: {
            modelId: 'claude-sonnet-4-5-20250929',
            temperature: 0.3, // Lower temperature for more focused fixes
          },
        }),
      });

      // Check for API errors
      if (!response.ok) {
        throw new Error('Failed to get suggestion');
      }

      // Read the SSE stream to get the AI response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      if (reader) {
        // Read chunks until the stream is done
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Decode the chunk and parse SSE events
          const text = decoder.decode(value);
          const lines = text.split('\n');

          for (const line of lines) {
            // SSE format: "data: {...}"
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);

            // "[DONE]" marks end of stream
            if (data === '[DONE]') break;

            try {
              const event = JSON.parse(data);
              // Accumulate text content from the AI response
              if (event.type === 'text') {
                fullText += event.content;
              }
            } catch {
              // Ignore JSON parse errors (incomplete chunks)
            }
          }
        }
      }

      // Set the full suggestion text
      setSuggestion(fullText);

      // Extract the code block from the response
      // Matches ```python or ```javascript code blocks
      const codeMatch = fullText.match(/```(?:python|javascript)?\n([\s\S]*?)```/);
      if (codeMatch) {
        setSuggestedCode(codeMatch[1].trim());
      }
    } catch {
      // Show error message if the request failed
      setSuggestion('Failed to get AI suggestion. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [boardId, code, error, language]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 overflow-hidden">
      {/* ========== ERROR HEADER ========== */}
      <div className="px-3 py-2 bg-red-100 border-b border-red-200">
        <div className="flex items-center gap-2">
          {/* Error icon */}
          <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {/* Error name and message */}
          <span className="font-semibold text-red-700 text-sm">
            {error.name}: {error.value}
          </span>
        </div>
      </div>

      {/* ========== TRACEBACK (collapsible) ========== */}
      {error.traceback && (
        <details className="px-3 py-2 border-b border-red-200">
          <summary className="text-xs text-red-600 cursor-pointer hover:text-red-700">
            Show traceback
          </summary>
          <pre className="mt-2 text-[10px] text-red-600 whitespace-pre-wrap font-mono overflow-x-auto">
            {error.traceback}
          </pre>
        </details>
      )}

      {/* ========== AI SUGGESTION SECTION ========== */}
      <div className="px-3 py-2">
        {/* Initial state: Show "Fix with AI" button */}
        {!suggestion && !isLoading && (
          <button
            onClick={requestFix}
            className="
              flex items-center gap-2 px-3 py-1.5
              text-sm font-medium text-purple-600
              bg-purple-50 hover:bg-purple-100
              rounded-lg transition-colors
            "
          >
            {/* Lightning bolt icon */}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Fix with AI
          </button>
        )}

        {/* Loading state: Show spinner */}
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-purple-600">
            {/* Spinning loader */}
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Getting AI suggestion...
          </div>
        )}

        {/* Suggestion received: Show explanation and code */}
        {suggestion && (
          <div className="space-y-2">
            {/* Explanation text (everything before the code block) */}
            <div className="text-sm text-gray-700 prose prose-sm max-w-none">
              {suggestion.split('```')[0]}
            </div>

            {/* Suggested code with "Apply Fix" button */}
            {suggestedCode && (
              <div className="relative">
                <pre className="text-xs font-mono bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto">
                  {suggestedCode}
                </pre>
                {/* Apply Fix button - puts code in editor for review */}
                <button
                  onClick={() => onApplyFix?.(suggestedCode)}
                  className="
                    absolute top-1 right-1 px-2 py-1
                    text-xs font-medium text-white
                    bg-purple-500 hover:bg-purple-600
                    rounded transition-colors
                  "
                >
                  Apply Fix
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
